/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { WEEK_DAYS } from '@sportninja/common/constants/weekDays'
import type { WeekDay } from '@sportninja/common/features/profile/finderSettings/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  weightMedium,
  weightSemiBold,
  paragraphSmall,
} from 'src/components/css'

interface Props {
  value: WeekDay[]
  onToggleDay: (day: WeekDay) => void
}

const DayChips: React.FC<Props> = ({ value, onToggleDay }) => (
  <div>
    <div css={labelStyle}>{capitalizeWords('Available Days')}</div>
    <div css={rowStyle}>
      {WEEK_DAYS.map((day) => {
        const active = value.includes(day.value)
        return (
          <button
            key={day.value}
            type='button'
            aria-pressed={active}
            css={[chipStyle, active && chipActiveStyle]}
            onClick={() => onToggleDay(day.value)}
          >
            {day.short}
          </button>
        )
      })}
    </div>
  </div>
)

export default DayChips

// ─── Styles ────────────────────────────────────────────────────────────────

const labelStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  ${font.body}
  color: ${colors.WHITE};
  margin-bottom: 8px;
`

const rowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

// Unselected: outlined (transparent fill + border + light text). Active:
// PRIMARY fill with dark text.
const chipStyle = css`
  ${paragraphSmall}
  ${weightMedium}
  ${font.body}
  /* Every day label is three characters, so the chips are sized to a fixed
     width rather than to their text — "Wed" is wider than "Fri" at the same
     padding, and a row of unequal chips reads as misaligned. */
  width: 56px;
  padding: 8px 0;
  text-align: center;
  border-radius: 8px;
  border: 1px solid ${colors.SOFT_STEEL};
  background-color: transparent;
  color: ${colors.WHITE};
  cursor: pointer;
`

const chipActiveStyle = css`
  ${weightSemiBold}
  background-color: ${colors.PRIMARY};
  border-color: ${colors.PRIMARY};
  color: ${colors.SECONDARY_300};
`
